import { DropdownItem } from '@base-app';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PillId } from '../components/CollectionAndSequencing/CollectionAndSequencing.types';
import {
  CollectionLocationSelectId,
  DemographicsBy,
  DemographicsByPropName,
  DemographicsData,
  DemographicsOptionType,
  GroupedDemographicsData,
  SampleStatusSelectId,
  SampleTypeSelectId,
} from '../components/CollectionAndSequencing/Demographics/Demographics.types';
import { STUDY_INSIGHTS_TRANSLATION } from '../constants';
import { groupDemographicsData } from '../utils';
import { UseDemographicsFilters, UseDemographicsFiltersProps } from '../types';

/**
 * @param data Demographics data to filter
 * @param selectedPillId Current section pill selected
 * @returns handleFilterChange method to update current filters
 * @returns reducedData filtered and reduced data comming from the custom hook param
 * @returns isFilteringData to manage UI rendering
 */
const useDemographicsFilters = ({ data, selectedPillId }: UseDemographicsFiltersProps): UseDemographicsFilters => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [filteredData, setFilteredData] = useState<DemographicsData>({
    byRaceEthnicity: [],
    byPreferredLanguage: [],
    byAgeRange: [],
    byBiologicalSex: [],
  });

  const [reducedData, setReducedData] = useState<GroupedDemographicsData>({
    byRaceEthnicity: [],
    byPreferredLanguage: [],
    byAgeRange: [],
    byBiologicalSex: [],
  });

  const [isFilteringData, setIsFilteringData] = useState<boolean>(true);

  const [sequencedSamplesFilters, setSequencedSamplesFilters] = useState<DropdownItem[]>([
    {
      id: CollectionLocationSelectId.ALL,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.all_collections_locations'),
    },
    {
      id: SampleStatusSelectId.ALL,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.all_sample_status'),
    },
    {
      id: SampleTypeSelectId.ALL,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.all_sample_type'),
    },
  ]);

  const [receivedSamplesFilters, setReceivedSamplesFilters] = useState<DropdownItem[]>([
    {
      id: CollectionLocationSelectId.ALL,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.all_collections_locations'),
    },
    {
      id: SampleTypeSelectId.ALL,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.all_sample_type'),
    },
  ]);

  const [awaitingSamplesFilters, setAwaitingSamplesFilters] = useState<DropdownItem[]>([
    {
      id: CollectionLocationSelectId.ALL,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.all_collections_locations'),
    },
  ]);

  const [currentFilters, setCurrentFilter] = useState<DropdownItem[]>(sequencedSamplesFilters);

  const filterData = (
    demographicsData: DemographicsBy[],
    filterIdToApply: string | number,
    filterIdToReturnAllItems: string,
    itemPropToFilter: string,
  ): DemographicsBy[] => {
    return demographicsData.filter((item) => {
      if (filterIdToApply === filterIdToReturnAllItems) {
        return item;
      } else if (item[itemPropToFilter as keyof DemographicsBy] === filterIdToApply) {
        return item;
      }
      return null;
    });
  };

  const filterDataForSequencedSamples = (demographicsByData: DemographicsBy[]): DemographicsBy[] => {
    const filteredByCollectionLocation: DemographicsBy[] = filterData(
      demographicsByData,
      sequencedSamplesFilters[0].id,
      CollectionLocationSelectId.ALL,
      DemographicsByPropName.COLLECTION_LOCATION,
    );
    const filteredByStatus: DemographicsBy[] = filterData(
      filteredByCollectionLocation,
      sequencedSamplesFilters[1].id,
      SampleStatusSelectId.ALL,
      DemographicsByPropName.STATUS,
    );
    const filteredBySampleType: DemographicsBy[] = filterData(
      filteredByStatus,
      sequencedSamplesFilters[2].id,
      SampleTypeSelectId.ALL,
      DemographicsByPropName.TYPE,
    );
    return filteredBySampleType;
  };

  const filterDataForReceivedSamples = (demographicsByData: DemographicsBy[]): DemographicsBy[] => {
    const filteredByCollectionLocation: DemographicsBy[] = filterData(
      demographicsByData,
      receivedSamplesFilters[0].id,
      CollectionLocationSelectId.ALL,
      DemographicsByPropName.COLLECTION_LOCATION,
    );
    const filteredBySampleType: DemographicsBy[] = filterData(
      filteredByCollectionLocation,
      receivedSamplesFilters[1].id,
      SampleTypeSelectId.ALL,
      DemographicsByPropName.TYPE,
    );
    return filteredBySampleType;
  };

  const filterDataForAwaitingSamples = (demographicsByData: DemographicsBy[]): DemographicsBy[] => {
    const filteredByCollectionLocation: DemographicsBy[] = filterData(
      demographicsByData,
      awaitingSamplesFilters[0].id,
      CollectionLocationSelectId.ALL,
      DemographicsByPropName.COLLECTION_LOCATION,
    );
    return filteredByCollectionLocation;
  };

  useEffect(() => {
    if (selectedPillId === PillId.SEQUENCED) {
      setFilteredData({
        byRaceEthnicity: filterDataForSequencedSamples(data.byRaceEthnicity),
        byPreferredLanguage: filterDataForSequencedSamples(data.byPreferredLanguage),
        byAgeRange: filterDataForSequencedSamples(data.byAgeRange),
        byBiologicalSex: filterDataForSequencedSamples(data.byBiologicalSex),
      });
    }

    if (selectedPillId === PillId.RECEIVED) {
      setFilteredData({
        byRaceEthnicity: filterDataForReceivedSamples(data.byRaceEthnicity),
        byPreferredLanguage: filterDataForReceivedSamples(data.byPreferredLanguage),
        byAgeRange: filterDataForReceivedSamples(data.byAgeRange),
        byBiologicalSex: filterDataForReceivedSamples(data.byBiologicalSex),
      });
    }

    if (selectedPillId === PillId.AWAITING) {
      setFilteredData({
        byRaceEthnicity: filterDataForAwaitingSamples(data.byRaceEthnicity),
        byPreferredLanguage: filterDataForAwaitingSamples(data.byPreferredLanguage),
        byAgeRange: filterDataForAwaitingSamples(data.byAgeRange),
        byBiologicalSex: filterDataForAwaitingSamples(data.byBiologicalSex),
      });
    }
  }, [data, sequencedSamplesFilters, receivedSamplesFilters, awaitingSamplesFilters]);

  useEffect(() => {
    if (filteredData !== undefined) {
      setReducedData({
        byRaceEthnicity: groupDemographicsData(filteredData.byRaceEthnicity),
        byPreferredLanguage: groupDemographicsData(filteredData.byPreferredLanguage),
        byAgeRange: groupDemographicsData(filteredData.byAgeRange),
        byBiologicalSex: groupDemographicsData(filteredData.byBiologicalSex),
      });

      setIsFilteringData(false);
    }
  }, [filteredData]);

  useEffect(() => {
    if (selectedPillId === PillId.SEQUENCED) setCurrentFilter(sequencedSamplesFilters);
    if (selectedPillId === PillId.RECEIVED) setCurrentFilter(receivedSamplesFilters);
    if (selectedPillId === PillId.AWAITING) setCurrentFilter(awaitingSamplesFilters);
  }, [selectedPillId]);

  const handleFilterChange = (item: DropdownItem): void => {
    const updatedFilters: DropdownItem[] = currentFilters.map((filter) => {
      if (filter.optionType === item.optionType) {
        filter.id = item.id;
        filter.label = item.label;
        filter.optionType = item.optionType;
      }
      return filter;
    });
    if (selectedPillId === PillId.SEQUENCED) setSequencedSamplesFilters(updatedFilters);
    if (selectedPillId === PillId.RECEIVED) setReceivedSamplesFilters(updatedFilters);
    if (selectedPillId === PillId.AWAITING) setAwaitingSamplesFilters(updatedFilters);
  };

  return { handleFilterChange, reducedData, isFilteringData };
};

export default useDemographicsFilters;
